import { useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { spellPc } from "@/theory/spell";
import { kickAudio, playSequence } from "@/audio/piano";

/**
 * Piano Intermediate · Lesson 2 — Mixolydian Improv Exercise.
 *
 * The Mixolydian mode = major scale with a flat 7. We learn its three
 * principal chords (♭VII – IV – I) as a plagal cadence in the left hand,
 * then improvise in the right with three different scales: Mixolydian
 * itself, the Major Blues, and the Minor Blues.
 */

const SCALES = [
  { id: "mixolydian",  name: "Mixolydian",  description: "1 2 3 4 5 6 ♭7 — major with a flat seventh.",                     semis: [0, 2, 4, 5, 7, 9, 10] },
  { id: "major-blues", name: "Major Blues", description: "1 2 ♭3 3 5 6 — pentatonic + the iconic blue note (♭3↔3).",       semis: [0, 2, 3, 4, 7, 9] },
  { id: "minor-blues", name: "Minor Blues", description: "1 ♭3 4 ♭5 5 ♭7 — minor pentatonic + the tritone (♭5).",          semis: [0, 3, 5, 6, 7, 10] },
];

/** ♭VII, IV, I as Jason voices them — semitones above the tonic (in F: G-B♭-E♭, F-B♭-D, F-A-C). */
const VOICINGS: number[][] = [[2, 5, 10], [0, 5, 9], [0, 4, 7]];

export default function PianoIntermediate2() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "2");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="F" availableQualities={["major"]}>
        <PIShell
          title="Mixolydian Improv Exercise"
          exerciseGoal="Three chords in the left hand. Three improv scales in the right. A one-bar gap to invent something."
          bpm={92}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [scaleIdx, setScaleIdx] = useState(0);
  const scale = SCALES[scaleIdx];

  const litHighlights: KeyHighlight[] = [
    ...scale.semis.map((s): KeyHighlight => ({ pc: (k.tonicPc + s) % 12, tone: "highlight" })),
    { pc: (k.tonicPc + 0) % 12,  tone: "root",  label: "I" },
    { pc: (k.tonicPc + 5) % 12,  tone: "voice", label: "IV" },
    { pc: (k.tonicPc + 10) % 12, tone: "bass",  label: "♭VII" },
  ];

  const playProgression = async () => {
    kickAudio();
    // Jason's voicing (lesson video ~03:50): "playing Eb is G, Bb, Eb,
    // F, Bb, D and then F, A, C" — inversions, so the hand barely moves.
    const chord = (voicing: number[]) =>
      voicing.map((s) => midiToPitchName(tonicMidi - 12 + s, k.useFlats));
    // Jason's chart (book page "Chord Pattern"): ♭VII on beat 1 (half note),
    // IV on beat 3 (dotted quarter), I on the "and" of 4, tied through the
    // whole next bar — the bar you improvise into. `time` is in seconds.
    const BPM = 92;
    const beat = 60 / BPM;
    await playSequence(
      [
        { time: 0,          notes: chord(VOICINGS[0]), duration: "2n" },
        { time: beat * 2,   notes: chord(VOICINGS[1]), duration: "4n." },
        { time: beat * 3.5, notes: chord(VOICINGS[2]), duration: "1n." },
      ],
      BPM
    );
  };

  const playScale = async () => {
    kickAudio();
    const up = scale.semis.map((s) => tonicMidi + s);
    const down = [...up].reverse().slice(1);
    const all = [...up, tonicMidi + 12, ...down];
    const BPM = 92;
    const eighth = 60 / BPM / 2;
    await playSequence(
      all.map((m, i) => ({
        time: i * eighth,
        notes: [midiToPitchName(m, k.useFlats)],
        duration: "8n",
      })),
      BPM
    );
  };

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={litHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel="Mixolydian map: scale tones plus I/IV/♭VII chord roots"
        />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          Cyan = the current improv scale. Gold = I (tonic). Pink ♭VII = the borrowed chord that
          defines Mixolydian. Click any key to hear it.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="Mixolydian = major minus its leading tone">
          <p>
            <strong>Mixolydian</strong> is a major scale with one tweak:
            the seventh is flattened. That single change pulls the music away
            from the lift-and-resolve of major-key pop into the open, modal
            world of rock, folk and a lot of contemporary music.
          </p>
          <p>
            In the key of <strong>{k.tonicName}</strong>, the seven notes are
            laid out on the keyboard above, with the <strong>I</strong>,
            <strong> IV</strong> and <strong>♭VII</strong> roots flagged. Those
            three notes are the chord roots we'll loop with the left hand.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="Left-hand chords" title="The plagal loop · ♭VII → IV → I">
          <p>
            All three are <strong>major</strong> triads. They're the
            "1 4 ♭7" of the Mixolydian mode — and the cadence ♭VII → IV → I
            is a chain of two <em>plagal</em> motions ("amen" cadences). It
            sounds calm, settled, anthem-like.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "So just get used to that. E flat, B flat, F."
          </PIQuote>
          <p>
            Jason's reference is the Beatles' <em>With a Little Help from My
            Friends</em>. Play the chords with inversions so they shift
            easily — his voicing in {k.tonicName}:{" "}
            <strong>
              {VOICINGS.map((v) => v.map((s) => spellPc((k.tonicPc + s) % 12, k.useFlats)).join("-")).join(" → ")}
            </strong>
            , played in and around middle C.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={playProgression}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
            >
              ▶ Play the progression in {k.tonicName}
            </button>
            <span className="self-center text-[12px] italic text-cyan-300/70">
              Half notes, plagal cadence ending on the tonic.
            </span>
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <PINoteImage
          src="/notes/piano-intermediate/finals/02-mixolydian-improv-page1.png"
          caption="Jason's chart — the chords of F Mixolydian: I, IV and ♭VII."
        />
      </FadeIn>

      <FadeIn>
        <PINoteImage
          src="/notes/piano-intermediate/finals/02-mixolydian-improv-page3.png"
          caption="Jason's chart — the inversions, and the paths between them that keep the hand still."
        />
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="The rhythm" title="Make a one-bar gap to improvise into">
          <p>Don't pump the chords on every beat. The exercise is built on a specific rhythm:</p>
          <p className="font-mono text-[14px] text-amber-200 my-2">
            ♭VII on 1 (half note) · IV on 3 (dotted quarter) · I on the "and" of 4, tied through the next bar
          </p>
          <ImprovClock />
          <p>
            That leaves you a <strong>full bar of held tonic</strong> to
            improvise into before the cycle restarts. The constraint is the
            point — improvising freely is harder than improvising inside a
            time-bound bar.
          </p>
          <PIInsight>
            <p>
              Improvisation isn't just "play whatever". It's <em>shape-making
              under constraint</em>. The one-bar gap forces you to commit to a
              musical idea and finish it before the chords come back.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <PINoteImage
          src="/notes/piano-intermediate/finals/02-mixolydian-improv-page5.png"
          caption="Jason's chart — the chord pattern: ♭VII on 1, IV on 3, I on the 'and' of 4, then a bar to improvise."
        />
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Right hand" title="Three improv scales">
          <p>
            All three live happily over the ♭VII–IV–I loop. Click between
            them to re-light the keyboard at the top, then press play to
            hear the scale up and back.
          </p>
          <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-3">
            {SCALES.map((s, i) => {
              const active = i === scaleIdx;
              return (
                <motion.button
                  key={s.id}
                  layout
                  onClick={() => setScaleIdx(i)}
                  className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                    active
                      ? "border-amber-400/60 bg-amber-500/10"
                      : "border-cyan-400/20 bg-cyan-500/5 hover:bg-cyan-500/15"
                  }`}
                >
                  <div className={`text-[14px] font-semibold ${active ? "text-amber-200" : "text-cyan-100"}`}>
                    {s.name}
                  </div>
                  <div className="mt-1 text-[11px] leading-snug text-cyan-300/80">{s.description}</div>
                </motion.button>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={playScale}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
            >
              ▶ Hear the {scale.name} up and back in {k.tonicName}
            </button>
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <PINoteImage
          src="/notes/piano-intermediate/finals/02-mixolydian-improv-page2.png"
          caption="Jason's chart — the plagal cadences on the circle of fifths."
        />
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Sing first, play after" title="The voice always knows">
          <p>
            Jason's tip — and this is the most important sentence of the
            lesson: <em>sing your improvisation first</em>. In the gap, hum
            or vocalise a phrase. Then catch it on the keys.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "Sometimes your fingers are running away, you know, even faster
            than your own brain it appears, you tend to play very robotic
            music or very forced music. But when you sing it, it can be a lot
            more meaningful and a lot more exciting."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="Loop the progression and improvise">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="2"
            label={`Mixolydian loop · ${k.tonicName}`}
            originalBpm={92}
          />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Sixteen minutes, four rungs">
          <PracticeLadder
            intro={
              <>
                The first three rungs introduce one improv scale at a time.
                The fourth rung mixes them — that's the real-world skill.
              </>
            }
            rungs={[
              { title: "Left hand alone",  minutes: 3, accent: "amber",   detail: "♭VII on 1, IV on 3, I on the 'and' of 4 — then hold it for the gap bar. Count the 'ands' out loud." },
              { title: "RH Mixolydian",     minutes: 4, accent: "cyan",    detail: "Walk the parent scale up and back. Let your phrases land on the I when the chords resolve." },
              { title: "RH Major Blues",    minutes: 4, accent: "rose",    detail: "Same loop, now using the major-blues scale. Lean into the ♭3 → 3 slide." },
              { title: "Minor Blues + free improv", minutes: 5, accent: "emerald", detail: "Add the minor blues. Mix all three scales freely, especially in the one-bar gap. Sing first." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* —— Bespoke visual: a circular clock for the 2-bar loop. The eight
       slices are eighth-note positions across two bars. Three slices show
       chord placements; the rest of the second bar is the IMPROV GAP, the
       region the student fills with melody. —— */
function ImprovClock() {
  // 2-bar loop = 8 beats. Use SVG arcs to mark where each chord lands.
  const r = 70;
  const cx = 80;
  const cy = 80;
  const segmentArc = (startBeat: number, endBeat: number, color: string, label: string, key: string) => {
    const a0 = (startBeat / 8) * 360 - 90;
    const a1 = (endBeat / 8) * 360 - 90;
    const toXY = (deg: number) => [
      cx + r * Math.cos((deg * Math.PI) / 180),
      cy + r * Math.sin((deg * Math.PI) / 180),
    ];
    const [x0, y0] = toXY(a0);
    const [x1, y1] = toXY(a1);
    const large = endBeat - startBeat > 4 ? 1 : 0;
    const path = `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`;
    const midDeg = (a0 + a1) / 2;
    const [tx, ty] = [
      cx + (r * 0.6) * Math.cos((midDeg * Math.PI) / 180),
      cy + (r * 0.6) * Math.sin((midDeg * Math.PI) / 180),
    ];
    return (
      <g key={key}>
        <path d={path} fill={color} stroke="#0F172A" strokeWidth={1} />
        <text x={tx} y={ty} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontFamily="Inter,system-ui" fontSize="11" fontWeight={700}>
          {label}
        </text>
      </g>
    );
  };

  return (
    <div className="my-4 flex items-center gap-5 rounded-2xl border border-cyan-400/30 bg-cyan-950/20 p-4">
      <svg width="160" height="160" viewBox="0 0 160 160">
        {/* slices */}
        {segmentArc(0, 2, "rgba(244,63,94,0.55)", "♭VII", "vii")}
        {segmentArc(2, 3.5, "rgba(34,211,238,0.55)", "IV", "iv")}
        {segmentArc(3.5, 4, "rgba(245,158,11,0.65)", "I", "i")}
        {segmentArc(4, 8, "rgba(16,185,129,0.40)", "GAP", "gap")}
        {/* tick marks every beat */}
        {Array.from({ length: 8 }, (_, b) => {
          const deg = (b / 8) * 360 - 90;
          const x1 = cx + (r - 8) * Math.cos((deg * Math.PI) / 180);
          const y1 = cy + (r - 8) * Math.sin((deg * Math.PI) / 180);
          const x2 = cx + (r + 4) * Math.cos((deg * Math.PI) / 180);
          const y2 = cy + (r + 4) * Math.sin((deg * Math.PI) / 180);
          return (
            <g key={b}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#fff" strokeWidth="1" opacity="0.5" />
              <text
                x={cx + (r + 14) * Math.cos((deg * Math.PI) / 180)}
                y={cy + (r + 14) * Math.sin((deg * Math.PI) / 180)}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#94A3B8"
                fontFamily="JetBrains Mono,monospace"
                fontSize="10"
              >
                {b + 1}
              </text>
            </g>
          );
        })}
        <circle cx={cx} cy={cy} r="3" fill="#0F172A" />
      </svg>
      <div className="flex-1">
        <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-amber-300">
          Two-bar loop
        </div>
        <ul className="mt-2 space-y-1 text-[13px] leading-snug text-cyan-50/95">
          <li><span className="inline-block h-2 w-2 rounded-full bg-rose-400" /> &nbsp;<strong>♭VII</strong> · beats 1–2</li>
          <li><span className="inline-block h-2 w-2 rounded-full bg-cyan-400" /> &nbsp;<strong>IV</strong> · beat 3 to the "and" of 4</li>
          <li><span className="inline-block h-2 w-2 rounded-full bg-amber-400" /> &nbsp;<strong>I</strong> · arrives on the "and" of 4</li>
          <li><span className="inline-block h-2 w-2 rounded-full bg-emerald-400" /> &nbsp;<strong>GAP</strong> · the whole second bar, over the held I → improvise here</li>
        </ul>
        <p className="mt-2 text-[12px] italic text-cyan-300/80">
          You always have a full bar to invent something before the next ♭VII.
        </p>
      </div>
    </div>
  );
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-md">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard · live</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
